import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowUpCircle,
  ShieldCheck,
  CheckCircle2,
  Undo2,
  ListTodo,
  CheckSquare,
  Zap,
  FileText,
  Mail,
  AlertTriangle,
} from 'lucide-react';
import { safeFetch } from '../../../lib/api';

export interface ActivityEntry {
  id: string;
  kind: 'stage' | 'override' | 'approval' | 'rollback' | 'task' | 'phase_action' | 'automation' | 'document' | 'email';
  at: string;
  actorUid: string | null;
  actorName: string | null;
  title: string;
  detail: string | null;
}

interface DrawerActivityFeedProps {
  uid: string;
  /** Bumped by the drawer on every (re)load; refetch when it changes. */
  refreshSignal?: number;
}

const KIND_VISUALS: Record<ActivityEntry['kind'], { icon: React.ReactNode; dot: string }> = {
  stage: { icon: <ArrowUpCircle className="h-3.5 w-3.5" />, dot: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  override: { icon: <ShieldCheck className="h-3.5 w-3.5" />, dot: 'bg-[var(--color-override-bg)] text-[var(--color-override)] border-[var(--color-override-border)]' },
  approval: { icon: <CheckCircle2 className="h-3.5 w-3.5" />, dot: 'bg-blue-50 text-blue-700 border-blue-200' },
  rollback: { icon: <Undo2 className="h-3.5 w-3.5" />, dot: 'bg-amber-50 text-amber-800 border-amber-200' },
  task: { icon: <ListTodo className="h-3.5 w-3.5" />, dot: 'bg-amber-50 text-amber-700 border-amber-200' },
  phase_action: { icon: <CheckSquare className="h-3.5 w-3.5" />, dot: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  automation: { icon: <Zap className="h-3.5 w-3.5" />, dot: 'bg-violet-50 text-violet-700 border-violet-200' },
  document: { icon: <FileText className="h-3.5 w-3.5" />, dot: 'bg-sky-50 text-sky-700 border-sky-200' },
  email: { icon: <Mail className="h-3.5 w-3.5" />, dot: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const PAGE_SIZE = 40;

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * The drawer's unified activity feed: every canonical record written
 * about this applicant — stage moves, gate overrides WITH their
 * reasons, approvals, rollbacks/demotions, task events, phase-action
 * executions, automation runs, document review events, emails —
 * newest first, each stamped with actor + time. Read-only; assembled
 * server-side from the canonical stores (no parallel event store).
 */
export const DrawerActivityFeed: React.FC<DrawerActivityFeedProps> = ({ uid, refreshSignal }) => {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: any = await safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/activity`);
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    setShowAll(false);
    fetchFeed();
  }, [fetchFeed]);

  const lastSignalRef = useRef(refreshSignal ?? 0);
  useEffect(() => {
    const s = refreshSignal ?? 0;
    if (s !== lastSignalRef.current) {
      lastSignalRef.current = s;
      fetchFeed();
    }
  }, [refreshSignal, fetchFeed]);

  if (loading && entries.length === 0) {
    return (
      <div className="space-y-2" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 bg-slate-100 rounded-xl animate-pulse" />
        ))}
        <span className="sr-only">Loading activity…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
        <p className="text-xs font-bold text-slate-600 flex-1">{error}</p>
        <button type="button" onClick={fetchFeed} className="text-xs font-bold text-indigo-600 underline cursor-pointer">
          Retry
        </button>
      </div>
    );
  }

  if (entries.length === 0) {
    return <p className="text-xs text-slate-500 font-medium">No activity recorded yet.</p>;
  }

  const visible = showAll ? entries : entries.slice(0, PAGE_SIZE);

  return (
    <div>
      <ol className="space-y-2.5">
        {visible.map((e) => {
          const visual = KIND_VISUALS[e.kind] || KIND_VISUALS.stage;
          return (
            <li key={e.id} className="flex items-start gap-2.5 text-xs">
              <span
                className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${visual.dot}`}
                aria-hidden="true"
              >
                {visual.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-800 leading-snug">{e.title}</p>
                <p className="text-slate-400 mt-0.5">
                  {fmtDateTime(e.at)}
                  {e.actorName ? ` · ${e.actorName}` : ''}
                </p>
                {e.detail && (
                  <p className="mt-1 rounded-lg bg-slate-50 border border-slate-200/70 px-2.5 py-1.5 text-[11px] text-slate-600 whitespace-pre-line">
                    {e.detail}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {entries.length > PAGE_SIZE && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-3 text-xs font-bold text-indigo-600 underline cursor-pointer"
        >
          Show all {entries.length} entries
        </button>
      )}
    </div>
  );
};
